import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import Headline from 'uiKit/texts/Headline'
import Search from 'uiKit/Search/Search'
import Table from 'uiKit/table/Table'
import TableRow from 'uiKit/table/TableRow'
import TableCell from 'uiKit/table/TableCell'
import Pagination from 'uiKit/table/Pagination'
import Select from 'react-select'
import DateControl from 'uiKit/DateControlNew'
import BreadCrumbs from 'uiKit/BreadCrumbs/BreadCrumbs'
import LoaderScreen from 'uiKit/loaders/loaderScreen'
import LoaderOverlay from 'uiKit/loaders/loaderOverlay'
import NoChartData from 'uiKit/NoChartData'
import * as S from '../../Dashboard.style'
import {
  Container as TotalUsersContainer,
  TabsContainer,
  Tab,
  FiltersRow,
  ManagerSelectWrap,
  PaginationSpacer,
} from './TotalUsers.style'
import { formatAccountStatus } from './AccountStatusFilter'
import { loadManagersList, loadDashboardOverviewUsers, exportDashboardOverviewUsersCsv } from '../../api/dashboard'
import { DateControlContext } from 'contexts/DateControlContext'
import { getDashboardDateApiParams, shouldShowAccountStatusForDateRange } from '../../helpers/dashboardDateParams'
import { useScrollToTop } from 'hooks/useScrollToTop'
import { canViewCarecoDashboard, getUserRole, ROLE } from '../../../../security'
import { isQcsBotPayload } from '../../../../constants/qcs'
import { isProfessionalsNavAllowed } from 'tabs/home/api/usageLimits'
import { InactiveSelectOption, InactiveTagOption, mapManagerSelectOptions } from '../InactiveTagSelectOption'

type UserRow = {
  id: number
  firstName: string
  lastName: string
  role: string
  manager: string | null
  archived?: boolean
  status: string
  count?: number
}

const searchInputStyle = {
  borderRadius: 8,
  width: '327px',
  height: '45px',
  padding: '14px 44px 19px 24px',
  minWidth: 240,
  flex: '0 1 327px',
  border: '1px solid rgba(226, 232, 234, 1)',
}

type Props = RouteComponentProps & { activeBot?: any, adminUser?: any }

export const TotalUsers: React.FC<Props> = ({ history, match, activeBot, adminUser }) => {
  useScrollToTop()

  const [userStatus, setUserStatus] = useState('')
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [page, setPage] = useState(0)
  const [manager, setManager] = useState<{ label: string; value: string } | null>(null)
  const isQcs = isQcsBotPayload(activeBot)
  const showRoleTabs = isProfessionalsNavAllowed(activeBot)
  const { botId: routeBotId } = (match as any)?.params || {}
  const botId = routeBotId || activeBot?.id
  const isCareManager = getUserRole(botId, adminUser?.botAccesses) === ROLE.MANAGER
  const showAdminTab = !(isQcs && isCareManager)
  const [selectedRole, setRole] = useState<string>(() => (showRoleTabs || !showAdminTab ? '' : 'ADMIN'))
  const role = !showAdminTab && selectedRole === 'ADMIN' ? '' : selectedRole
  const isCarecoAdmin = useMemo(
    () => canViewCarecoDashboard(botId, adminUser?.botAccesses),
    [botId, adminUser],
  )
  const hideFrequency = isQcs
  const [rows, setRows] = useState<UserRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const {
    dateRange: { startDate, endDate },
    frequency,
  } = useContext(DateControlContext)
  const showAccountStatus = shouldShowAccountStatusForDateRange(isCarecoAdmin, frequency)
  const prevDatesRef = useRef({ start: startDate?.valueOf?.(), end: endDate?.valueOf?.() })
  const lastFetchKeyRef = useRef<string | null>(null)

  // Keep the selected tab available for this role and package.
  useEffect(() => {
    if (!showAdminTab) {
      if (selectedRole === 'ADMIN') setRole('')
      return
    }
    if (showRoleTabs || role === 'ADMIN') return
    setRole('ADMIN')
    setPage(0)
  }, [showRoleTabs, showAdminTab, selectedRole, role])

  // Get column titles based on selected role
  const getColumnTitles = () => {
    let columns
    switch (role) {
      case 'SUPERVISOR': // Managers
        columns = [
          { title: 'First name', maxWidth: 180 },
          { title: 'Last name', maxWidth: 180 },
          { title: 'No. of supervisees', maxWidth: 200 },
          { title: 'Manager', maxWidth: 220 },
          {
            title: 'Account status',
            maxWidth: 160,
            tooltipPlace: 'left',
            tooltipText:
              'Displays the current account status of each user, showing active users and inactive users who have been deleted.',
          },
        ]
        break
      case 'CARER': // Carers
        columns = [
          { title: 'First name', maxWidth: 180 },
          { title: 'Last name', maxWidth: 240 },
          { title: 'Manager', maxWidth: 220 },
          {
            title: 'Account status',
            maxWidth: 160,
            tooltipPlace: 'left',
            tooltipText:
              'Displays the current account status of each user, showing active users and inactive users who have been deleted.',
          },
        ]
        break
      case 'ADMIN': // Admins
        columns = [
          { title: 'Full name', maxWidth: 240 },
          { title: 'Role', maxWidth: 140 },
          {
            title: 'Account status',
            maxWidth: 160,
            tooltipPlace: 'left',
            tooltipText:
              'Displays the current account status of each user, showing active users and inactive users who have been deleted.',
          },
        ]
        break
      default:
        columns = [
          { title: 'First name', maxWidth: 180 },
          { title: 'Last name', maxWidth: 180 },
          { title: 'Role', maxWidth: 140 },
          { title: 'Manager', maxWidth: 220 },
          {
            title: 'Account status',
            maxWidth: 160,
            tooltipPlace: 'left',
            tooltipText:
              'Displays the current account status of each user, showing active users and inactive users who have been deleted.',
          },
        ]
    }
    return showAccountStatus
      ? columns
      : columns.filter(column => column.title !== 'Account status')
  }

  const showArchiveStatus = isQcs
  const columnTitles = [
    ...getColumnTitles(),
  ]

  const [managerOptions, setManagerOptions] = useState<InactiveSelectOption[]>([
    { label: 'All Managers', value: '', isInactive: false },
  ])

  const loadManagersOnOpen = () => {
    if (!botId) return

    loadManagersList(botId).then((res: any[]) => {
      if (Array.isArray(res)) {
        setManagerOptions(mapManagerSelectOptions(res))
      }
    })
  }

  useEffect(() => {
    setManagerOptions([{ label: 'All Managers', value: '', isInactive: false }])
    setManager(null)
    setPage(0)
    lastFetchKeyRef.current = null
  }, [botId])

  const buildOverviewParams = (pageOverride = page) => {
    const params: any = { page: pageOverride + 1, size: 10 }
    if (debouncedQuery) params.search = debouncedQuery
    if (manager?.value && role !== 'ADMIN') params.managerId = manager.value
    if (role) params.type = role
    if (showArchiveStatus && userStatus) params.archivedStatus = userStatus
    return params
  }

  useEffect(() => {
    if (!botId) return

    const start = startDate?.valueOf?.()
    const end = endDate?.valueOf?.()
    const datesChanged = prevDatesRef.current.start !== start || prevDatesRef.current.end !== end
    const effectivePage = datesChanged ? 0 : page

    if (datesChanged) {
      prevDatesRef.current = { start, end }
      if (page !== 0) {
        setPage(0)
      }
    }

    const fetchKey = [
      botId,
      effectivePage,
      frequency,
      start,
      end,
      debouncedQuery,
      manager?.value || '',
      showArchiveStatus ? userStatus : '',
      role,
    ].join('|')

    if (lastFetchKeyRef.current === fetchKey) return
    lastFetchKeyRef.current = fetchKey

    const snapshot = {
      botId,
      page: effectivePage,
      start,
      end,
      query: debouncedQuery,
      manager: manager?.value || '',
      role: role || '',
    }
    // Only show loading on initial load and bot/date/page changes, not on filter typing/changes
    const prev = (loadDashboardOverviewUsers as any)._prevSnap
    const isFirstLoad = !prev
    const pageOrDateChanged =
      isFirstLoad ||
      prev.botId !== snapshot.botId ||
      prev.page !== snapshot.page ||
      prev.start !== snapshot.start ||
      prev.end !== snapshot.end
    setLoading(true)

    const dateParams = getDashboardDateApiParams(frequency, startDate, endDate, isCarecoAdmin)
    loadDashboardOverviewUsers(botId, {
      ...dateParams,
      ...buildOverviewParams(effectivePage),
    })
      .then((res: any) => {
        if (res && Array.isArray(res.content)) {
          const mapped: UserRow[] = res.content.map((u: any, idx: number) => ({
            id: idx + 1,
            firstName: u.firstName,
            lastName: u.lastName,
            role: u.role.toLowerCase(),
            manager: u.managerName || '-',
            status: u.userStatus || u.status,
            archived: u.archived,
            count: u.superviseeCount,
          }))
          setRows(mapped)
          setTotalCount(Number(res.totalElements) || mapped.length)
        } else {
          setRows([])
          setTotalCount(0)
        }
      })
      .catch(() => {
        setRows([])
        setTotalCount(0)
      })
      .finally(() => {
        (loadDashboardOverviewUsers as any)._prevSnap = snapshot
        setLoading(false)
      })
  }, [botId, page, startDate, endDate, frequency, debouncedQuery, manager, role, userStatus, isQcs, showArchiveStatus])

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query)
      setPage(0)
    }, 500)
    return () => clearTimeout(handle)
  }, [query])

  const selectStyles = {
    control: base => ({
      ...base,
      minHeight: 45,
      height: 45,
      borderRadius: 10,
      borderColor: 'var(--color-input-stroke)',
      '&:hover': { borderColor: 'var(--color-input-stroke)' },
    }),
    valueContainer: base => ({ ...base, height: 45, padding: '0 12px' }),
    indicatorsContainer: base => ({ ...base, height: 45 }),
    menu: base => ({ ...base, zIndex: 5 }),
    option: (base, state) => ({
      ...base,
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflow: 'hidden',
      backgroundColor: state.isSelected ? '#E8F4FC' : base.backgroundColor,
      color: state.isSelected ? '#111827' : base.color,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#E8F4FC',
      },
      '&:hover': {
        backgroundColor: state.isSelected ? '#E8F4FC' : '#F3F4F6',
      },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
  }

  const shouldCloseOnScroll = (event: any) => {
    const target = (event && (event.target as HTMLElement)) || null
    if (!target) return true
    const withinMenu =
      target.closest('.users-select__menu')
      || target.closest('.users-select__menu-list')
      || target.closest('.account-status-select__menu')
      || target.closest('.account-status-select__menu-list')
    return !withinMenu
  }

  const handleDashboardClick = () => {
    // Navigate back to the dashboard base, similar to Overview's navigation style
    const currentUrl = (match as any)?.url || ''
    const dashboardBase = currentUrl.replace(/\/total-users$/, '') || '/dashboard'
    history.push(dashboardBase)
  }

  const handleExport = async () => {
    if (!botId) return
    try {
      setIsExporting(true)
      const dateParams = getDashboardDateApiParams(frequency, startDate, endDate, isCarecoAdmin)
      const blob = await exportDashboardOverviewUsersCsv(botId, {
        ...dateParams,
        ...buildOverviewParams(),
      })

      if (!blob) return
      const url = window.URL.createObjectURL(blob as any)
      const a = document.createElement('a')
      a.href = url
      a.download = 'dashboard-overview.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      // Error feedback handled in API
    } finally {
      setIsExporting(false)
    }
  }

  const renderBody = (() => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <LoaderScreen size={{ circleSize: 60, border: 8 }} />
        </div>
      )
    }
    if (rows.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
          <NoChartData text={'No users found'} isUserData={true} />
        </div>
      )
    }
    return (
      <>
        <Table titles={columnTitles}>
          {rows.map((row, idx) => {
            // Render cells based on role
            const renderCells = () => {
              switch (role) {
                case 'SUPERVISOR': // Managers
                  return (
                    <>
                      <TableCell style={{ maxWidth: columnTitles[0].maxWidth }}>{row.firstName}</TableCell>
                      <TableCell style={{ maxWidth: columnTitles[1].maxWidth }}>{row.lastName || '-'}</TableCell>
                      <TableCell style={{ maxWidth: columnTitles[2].maxWidth }}>{row.count || 0}</TableCell>
                      <TableCell style={{ maxWidth: columnTitles[3].maxWidth }}>{row.manager}</TableCell>
                      {showAccountStatus && (
                        <TableCell style={{ maxWidth: columnTitles[4].maxWidth }}>
                          {formatAccountStatus(row.status)}
                        </TableCell>
                      )}
                    </>
                  )
                case 'CARER': // Carers
                  return (
                    <>
                      <TableCell style={{ maxWidth: columnTitles[0].maxWidth }}>{row.firstName}</TableCell>
                      <TableCell style={{ maxWidth: columnTitles[1].maxWidth }}>{row.lastName || '-'}</TableCell>
                      <TableCell style={{ maxWidth: columnTitles[2].maxWidth }}>{row.manager}</TableCell>
                      {showAccountStatus && (
                        <TableCell style={{ maxWidth: columnTitles[3].maxWidth }}>
                          {formatAccountStatus(row.status)}
                        </TableCell>
                      )}
                    </>
                  )
                case 'ADMIN': // Admins
                  return (
                    <>
                      <TableCell style={{ maxWidth: columnTitles[0].maxWidth }}>{row.firstName}</TableCell>
                      <TableCell style={{ maxWidth: columnTitles[1].maxWidth, textTransform: 'capitalize' }}>
                        {row.role === 'supervisor'
                          ? 'Manager' : row.role === 'manager' ? 'Care Manager'
                            : row.role.replace('_', ' ')}
                      </TableCell>
                      {showAccountStatus && (
                        <TableCell style={{ maxWidth: columnTitles[2].maxWidth }}>
                          {formatAccountStatus(row.status)}
                        </TableCell>
                      )}
                    </>
                  )
                default:
                  // All users
                  return (
                    <>
                      <TableCell style={{ maxWidth: columnTitles[0].maxWidth }}>{row.firstName}</TableCell>
                      <TableCell style={{ maxWidth: columnTitles[1].maxWidth }}>{row.lastName || '-'}</TableCell>
                      <TableCell style={{ maxWidth: columnTitles[2].maxWidth, textTransform: 'capitalize' }}>
                        {row.role === 'supervisor' ? 'Manager' : row.role}
                      </TableCell>
                      <TableCell style={{ maxWidth: columnTitles[3].maxWidth }}>{row.manager}</TableCell>
                      {showAccountStatus && (
                        <TableCell style={{ maxWidth: columnTitles[4].maxWidth }}>
                          {formatAccountStatus(row.status)}
                        </TableCell>
                      )}
                    </>
                  )
              }
            }

            return (
              <TableRow key={row.id} style={{ background: idx % 2 ? 'var(--color-table-row-primary)' : undefined }}>
                {renderCells()}
              </TableRow>
            )
          })}
        </Table>
        <PaginationSpacer>
          <Pagination
            page={page}
            rowsPerPage={10}
            count={totalCount}
            onChangePage={(_, nextPage) => setPage(nextPage)}
          />
        </PaginationSpacer>
      </>
    )
  })()

  return (
    <TotalUsersContainer>
      {isExporting && <LoaderOverlay />}
      <PaginationSpacer mt={30}>
        <BreadCrumbs items={[{ label: 'Dashboard', onClick: handleDashboardClick }, { label: 'Total users' }]} />
      </PaginationSpacer>
      <S.HeadlineWrap>
        <Headline title={`Total users (${totalCount})`} />
        <DateControl
          isCarecoAdmin={isQcs || isCarecoAdmin}
          handleExport={isQcs || isCarecoAdmin ? handleExport : undefined}
          hideFrequency={hideFrequency}
        />
      </S.HeadlineWrap>
      <TabsContainer>
        {showRoleTabs && (
          <>
            <Tab active={role === ''} onClick={() => { setRole(''); setPage(0) }}>
              All users
            </Tab>
            <Tab active={role === 'SUPERVISOR'} onClick={() => { setRole('SUPERVISOR'); setPage(0) }}>
              Managers
            </Tab>
            <Tab active={role === 'CARER'} onClick={() => { setRole('CARER'); setPage(0) }}>
              Carers
            </Tab>
          </>
        )}
        {showAdminTab && (
          <Tab active={role === 'ADMIN'} onClick={() => { setRole('ADMIN'); setPage(0) }}>
            Admins
          </Tab>
        )}
      </TabsContainer>
      <FiltersRow>
        <Search
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by name"
          inputStyle={searchInputStyle}
        />
        {role !== 'ADMIN' && (
          <ManagerSelectWrap>
            <Select
              styles={selectStyles}
              options={managerOptions}
              value={manager || managerOptions[0]}
              onChange={opt => {
                setManager(opt as any)
                setPage(0)
              }}
              onMenuOpen={loadManagersOnOpen}
              isSearchable={false}
              closeMenuOnScroll={shouldCloseOnScroll}
              classNamePrefix="users-select"
              menuPosition={'absolute'}
              components={{ Option: InactiveTagOption }}
            />
          </ManagerSelectWrap>
        )}
      </FiltersRow>
      {renderBody}
    </TotalUsersContainer>
  )
}

const mapStateToProps = (state: any) => ({
  activeBot: state.activeBot,
  adminUser: state.adminUser,
})

export default withRouter(connect(mapStateToProps)(TotalUsers))
